// Conversational discovery field primitives — dark FleetOS aesthetic.
// Built to feel like a guided conversation, not a survey grid.

export function SectionCard({ step, title, subtitle, children, accent = false }) {
  return (
    <div
      data-testid={`section-${title?.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`rounded-2xl border p-5 sm:p-7 transition-colors ${
        accent ? "border-[#00FF66]/40 bg-[#00FF66]/[0.04]" : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="mb-5 flex items-start gap-3">
        {step && (
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white">
            {step}
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white sm:text-xl">{title}</h2>
          {subtitle && <p className="mt-1 text-sm leading-relaxed text-white/45">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

export function Hint({ children }) {
  return (
    <p className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm italic leading-relaxed text-white/55">
      <span className="mr-1.5 not-italic text-[#00FF66]">“</span>{children}<span className="ml-0.5 not-italic text-[#00FF66]">”</span>
    </p>
  );
}

// Conversational prompt list — each question in English with a colloquial Tamil
// line below it so agents can ask it naturally on the call.
export function Prompts({ items, label = "Ask, conversationally" }) {
  return (
    <div className="space-y-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#00FF66]/80">{label}</p>
      {items.map((q, i) => (
        <div key={i} className="border-l-2 border-[#00FF66]/30 pl-3">
          <p className="text-sm leading-snug text-white/75">“{q.en}”</p>
          {q.ta && <p className="mt-0.5 text-sm leading-snug text-white/45" lang="ta">{q.ta}</p>}
        </div>
      ))}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-white/80">{label}</label>}
      {children}
    </div>
  );
}

export function TextField({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <Field label={label}>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-white/12 bg-black/40 px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-[#00FF66]/60 focus:ring-1 focus:ring-[#00FF66]/30"
      />
    </Field>
  );
}

export function Notes({ value, onChange, testid }) {
  return (
    <Field label="Notes (capture their exact words)">
      <textarea
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder="Jot down anything they said freely…"
        className="w-full resize-y rounded-xl border border-white/12 bg-black/40 px-4 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-[#00FF66]/60 focus:ring-1 focus:ring-[#00FF66]/30"
      />
    </Field>
  );
}

export function ChoiceChips({ label, options, value, onChange, testid }) {
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const active = value === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(active ? "" : o)}
              className={`rounded-full border px-3.5 py-1.5 text-sm transition ${
                active
                  ? "border-[#00FF66] bg-[#00FF66] font-medium text-black"
                  : "border-white/15 bg-white/[0.02] text-white/70 hover:border-white/35 hover:text-white"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

export function MultiChips({ label, options, values = [], onToggle, testid }) {
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const active = values.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              className={`rounded-full border px-3.5 py-1.5 text-sm transition ${
                active
                  ? "border-white/70 bg-white/90 font-medium text-black"
                  : "border-white/15 bg-white/[0.02] text-white/70 hover:border-white/35 hover:text-white"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </Field>
  );
}
